import { EventPayload } from '../../../../types/GTM/EventPayload';
import { ECommerceItem } from '../../../../types/GTM/ECommerceItem';
import { NAFGTMEvent } from '../../../../types/GTM/NAFGTMEvent';
import { PromotionItem } from '../../../../types/GTM/PromotionItem';

const DEFAULT_DEBOUNCE_MS = 300;
const batchableEvents = new Set<NAFGTMEvent>([NAFGTMEvent.viewItem, NAFGTMEvent.viewPromotion]);

type BatchedEventState = {
  event: EventPayload;
  dataLayer: EventPayload[];
  timeoutId: ReturnType<typeof setTimeout>;
};

const batchedEvents = new Map<NAFGTMEvent, BatchedEventState>();

type EcommerceEventPayload = EventPayload & {
  ecommerce: {
    items: ECommerceItem[];
  };
};

type PromotionEventPayload = EventPayload & {
  ecommerce: {
    items: PromotionItem[];
  };
};

const getEventKey = (eventName?: EventPayload['event']): NAFGTMEvent | null => {
  if (!eventName) {
    return null;
  }

  const typedEvent = eventName as NAFGTMEvent;
  return batchableEvents.has(typedEvent) ? typedEvent : null;
};

const mergeEcommerceItems = (
  existing: EcommerceEventPayload,
  incoming: EcommerceEventPayload,
): EcommerceEventPayload => ({
  ...existing,
  ecommerce: {
    ...existing.ecommerce,
    ...incoming.ecommerce,
    items: [...existing.ecommerce.items, ...incoming.ecommerce.items],
  },
});

const mergePromotionItems = (
  existing: PromotionEventPayload,
  incoming: PromotionEventPayload,
): PromotionEventPayload => ({
  ...existing,
  ecommerce: {
    ...existing.ecommerce,
    ...incoming.ecommerce,
    items: [...existing.ecommerce.items, ...incoming.ecommerce.items],
  },
});

export const queueGTMEvent = (dataLayer: EventPayload[], event: EventPayload, debounceMs = DEFAULT_DEBOUNCE_MS) => {
  const eventKey = getEventKey(event.event);
  const items = event.ecommerce?.items;

  if (!eventKey || !items || items.length === 0) {
    dataLayer.push(event);
    return;
  }

  const existing = batchedEvents.get(eventKey);
  if (existing) {
    clearTimeout(existing.timeoutId);
  }

  let mergedEvent: EventPayload;
  if (eventKey === NAFGTMEvent.viewItem) {
    mergedEvent = existing
      ? mergeEcommerceItems(existing.event as EcommerceEventPayload, event as EcommerceEventPayload)
      : (event as EcommerceEventPayload);
  } else {
    mergedEvent = existing
      ? mergePromotionItems(existing.event as PromotionEventPayload, event as PromotionEventPayload)
      : (event as PromotionEventPayload);
  }
  const timeoutId = setTimeout(() => {
    const pending = batchedEvents.get(eventKey);
    if (!pending) {
      return;
    }

    pending.dataLayer.push(pending.event);
    batchedEvents.delete(eventKey);
  }, debounceMs);

  batchedEvents.set(eventKey, { event: mergedEvent, dataLayer, timeoutId });
};
